"use client";

import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { getInnerQueryData } from "@nimble/react-requests/data/response-utils";
import { useCallback } from "react";

import ActionRequirementsModalContentView, {
  type ActionType,
} from "@/components/shared/dialogs/action-requirements-modal/action-requirements-modal-content.view";
import ActionRequirementsModalSkeleton from "@/components/shared/dialogs/action-requirements-modal/action-requirements-modal-skeleton";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import useSchoolData from "@/hooks/data/use-school-data";
import useSignInDrawer from "@/hooks/use-sign-in-drawer";

interface ActionRequirementsModalContentContainerProps {
  schoolId: string;
  actionType: ActionType;
  onClose: () => void;
}

export function ActionRequirementsModalContentContainer({
  schoolId,
  actionType,
  onClose,
}: ActionRequirementsModalContentContainerProps) {
  const schoolQuery = useSchoolData({ ncesId: schoolId });
  const schoolName = getInnerQueryData(schoolQuery)?.name;
  const { openDrawer } = useSignInDrawer();

  const handleGetStarted = useCallback(() => {
    openDrawer("1");
  }, [openDrawer]);

  const handleMaybeLater = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <WhenAllReady
      queries={[schoolQuery]}
      loader={<ActionRequirementsModalSkeleton />}
    >
      {schoolName && (
        <ActionRequirementsModalContentView
          schoolName={schoolName}
          actionType={actionType}
          onGetStarted={handleGetStarted}
          onMaybeLater={handleMaybeLater}
        />
      )}
    </WhenAllReady>
  );
}

interface ActionRequirementsModalContainerProps
  extends ActionRequirementsModalContentContainerProps {
  isOpen: boolean;
}

export default function ActionRequirementsModalContainer({
  schoolId,
  isOpen,
  actionType,
  onClose,
}: ActionRequirementsModalContainerProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-md p-0 gap-0 overflow-hidden"
        showCloseButton={true}
      >
        <ActionRequirementsModalContentContainer
          schoolId={schoolId}
          actionType={actionType}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}
